/**
 * Attendance records, with the two things staff can still do to one.
 *
 * Shared by the activity screen and the attendance queue, so a row of hours
 * reads the same wherever staff meet it.
 *
 * **There is no decision here any more.** Approve and Reject lived in exactly
 * one place - the queue - and on 8 Oct 2026 Parinaam removed the step itself:
 * recording hours approves them. What is left is Edit and History, which were
 * always about the figures rather than the verdict, and they are offered on
 * every row rather than on the pending ones.
 *
 * **Adjusting the hours was always a different thing from deciding** (M2,
 * SS1.15-1.16), which is why that pair survives the removal unchanged. An
 * approved record whose duration was mistyped is the commonest thing there is
 * to fix, and fixing it never un-approved anything.
 *
 * Rows decided under the old workflow keep their status and their history, so
 * the Status column stays: it is how a rejected record from September still
 * explains why it is not in anybody total.
 *
 * **Every row can show its own history** (M2, S11.53). Opened on demand rather
 * than rendered inline: a table of twenty rows firing twenty audit queries to
 * display twenty things nobody is reading is not a history panel, it is a load
 * test.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AttendanceDetail } from '@parinaam-vms/shared';
import { Button } from '../common/Button';
import { DataTable } from '../tables/DataTable';
import type { Column } from '../tables/DataTable';
import { formatDate, orDash } from '../../utils/format';
import { AdjustHours } from './AdjustHours';
import { VolunteerHours } from './VolunteerHours';
import styles from './AttendanceTable.module.css';

export interface AttendanceTableProps {
  rows: AttendanceDetail[];
  /** Hide the volunteer column on a volunteer's own profile. */
  showVolunteer?: boolean;
  /** Hide the activity column on an activity's own screen. */
  showActivity?: boolean;
}

export function AttendanceTable({
  rows,
  showVolunteer = true,
  showActivity = true,
}: AttendanceTableProps) {
  const [adjusting, setAdjusting] = useState<string | null>(null);

  // Only one panel open at a time, and clicking the button again closes it. Two
  // open panels on one screen is two records' worth of detail with nothing saying
  // which is which.
  const toggleAdjust = (id: string) =>
    setAdjusting((current) => (current === id ? null : id));

  const columns: Column<AttendanceDetail>[] = [
    { key: 'date', header: 'Date', render: (row) => formatDate(row.attendance_date) },
    ...(showVolunteer
      ? [
          {
            key: 'volunteer',
            header: 'Volunteer',
            render: (row: AttendanceDetail) => (
              <Link to={`/volunteers/${row.volunteer_id}`}>{row.volunteer_name}</Link>
            ),
          },
        ]
      : []),
    ...(showActivity
      ? [
          {
            key: 'activity',
            header: 'Activity',
            render: (row: AttendanceDetail) => (
              <Link to={`/activities/${row.activity_id}`}>{row.activity_name}</Link>
            ),
          },
        ]
      : []),
    /*
     * Which occurrence, not just which activity (9 Oct 2026).
     *
     * An activity runs several sessions and a volunteer may be at more than
     * one, so "Read to Rise, 2h" was ambiguous on exactly the rows somebody
     * goes looking at — the repeated ones. Shown on the activity screen too,
     * where `showActivity` is false: that screen narrows to one activity and
     * the session is then the only thing separating its rows.
     */
    {
      key: 'session',
      header: 'Session',
      render: (row) => <Link to={`/sessions/${row.session_id}`}>{row.session_name}</Link>,
    },
    {
      key: 'duration',
      header: 'Duration',
      numeric: true,
      // Minutes are the stored contract (Plan D9); '2h 30m' is what a person
      // reads. For a group record it also shows the multiplication, because the
      // duration alone would read as one person's afternoon (M3, Plan D15).
      render: (row) => (
        <span className={styles.duration}>
          <VolunteerHours record={row} />
          {row.override_flag && (
            <span className={styles.override} title={row.override_reason ?? 'Manually adjusted'}>
              adjusted
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (row) => <span className={styles.notes}>{orDash(row.notes)}</span>,
    },
    {
      key: 'actions',
      header: '',
      /*
       * Edit and History, and nothing else (Parinaam, 8 Oct 2026).
       *
       * Approve and Reject lived here, shown on a PENDING row in the
       * attendance queue. Recording hours approves them now, so there is no
       * pending row to decide and no decision to offer - what is left is the
       * pair that was always about the figures rather than the verdict.
       *
       * The same two on every row, whatever its status. A record approved or
       * rejected under the old workflow still has history worth reading, and
       * an approved record whose duration was mistyped is still the commonest
       * thing there is to fix.
       */
      render: (row) => (
        <span className={styles.actions}>
          <Button onClick={() => toggleAdjust(row.attendance_id)}>Edit</Button>
        </span>
      ),
    },
  ];

  return (
    <>
      <DataTable<AttendanceDetail>
        caption="Attendance"
        rows={rows}
        getRowKey={(row) => row.attendance_id}
        columns={columns}
        /*
          Both panels open BENEATH the row rather than inside its actions cell.
          They were in the cell until the history panel made the cost obvious:
          the table scrolled sideways to fit them and took the date and the
          volunteer's name off-screen, so the panel describing a record no longer
          showed you which record it was.
        */
        renderExpanded={(row) => {
          if (adjusting === row.attendance_id) {
            return <AdjustHours record={row} onClose={() => setAdjusting(null)} />;
          }
          return null;
        }}
      />
    </>
  );
}

/*
 * `RowHistory` stood here until 9 Oct 2026 — one record's audit trail, opened
 * from its row (M2, §11.53). Parinaam asked for the History button to go from
 * every attendance record, so the panel it opened went with it.
 *
 * `GET /attendance/:id/history` is untouched and still returns the trail, and
 * every correction and override still writes its audit row. What has gone is
 * the way staff read them from this table. If it is wanted back, this is the
 * component to restore rather than rebuild.
 */
